(() => {
  const normalize = (value = '') => String(value).toLowerCase().replace(/[^a-z0-9]/g, '');

  const money = (cents, currency) => new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
    style: 'currency',
    currency: currency || 'USD',
    minimumFractionDigits: 2
  }).format(Number(cents || 0) / 100);

  const initHero = (hero) => {
    if (!hero || hero.dataset.commerceReady === 'true') return;
    hero.dataset.commerceReady = 'true';

    const currency = hero.dataset.currency || 'USD';
    const root = `${hero.dataset.shopRoot || window.Shopify?.routes?.root || '/'}`.replace(/\/?$/, '/');
    const scene = hero.querySelector('[data-hero-scene]');
    const background = hero.querySelector('[data-hero-media]');
    const items = [...hero.querySelectorAll('[data-stage-product]')];
    const rail = [...hero.querySelectorAll('[data-rail-product]')];
    const previous = hero.querySelector('[data-stage-prev]');
    const next = hero.querySelector('[data-stage-next]');
    const counter = hero.querySelector('[data-stage-current]');
    const toast = hero.querySelector('[data-commerce-toast]');
    const dockTitle = hero.querySelector('[data-dock-title]');
    const dockModel = hero.querySelector('[data-dock-model]');
    const dockStatus = hero.querySelector('[data-dock-status]');
    const dockPrice = hero.querySelector('[data-dock-price]');
    const dockUnit = hero.querySelector('[data-dock-unit]');
    const dockUrl = hero.querySelector('[data-dock-url]');
    const dockVariant = hero.querySelector('[data-dock-variant]');
    const dockMoq = hero.querySelector('[data-dock-moq]');
    const quantity = hero.querySelector('[data-product-quantity]');
    const add = hero.querySelector('[data-add-to-cart]');
    const addLabel = hero.querySelector('[data-add-label]');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const finePointer = window.matchMedia('(pointer: fine)').matches;
    const productCache = new Map();
    const loading = new Map();
    let active = 0;
    let toastTimer;
    let autoTimer;
    let touchStart = null;
    let userInteracted = false;

    const announce = (message) => {
      if (!toast) return;
      toast.textContent = message;
      toast.classList.add('is-visible');
      clearTimeout(toastTimer);
      toastTimer = setTimeout(() => toast.classList.remove('is-visible'), 3200);
    };

    const searchUrl = (model) => `${root}search?q=${encodeURIComponent(model)}&type=product`;

    const minimumQuantity = (product, fallback) => {
      const tags = Array.isArray(product?.tags) ? product.tags : String(product?.tags || '').split(',');
      for (const tag of tags) {
        const match = String(tag).match(/^moq(?:-|_|:|\s)+(\d+)$/i);
        if (match) return Math.max(1, Number(match[1]));
      }
      return Math.max(1, Number(fallback || 1));
    };

    const selectedVariant = () => {
      const state = productCache.get(active);
      if (!state?.product) return null;
      return state.product.variants.find((variant) => String(variant.id) === String(dockVariant?.value)) || state.product.variants[0];
    };

    const normalizeQuantity = (value) => {
      const minimum = Number(quantity?.min || 1);
      return Math.max(minimum, Math.round(Number(value) || minimum));
    };

    const refreshPrice = () => {
      const variant = selectedVariant();
      if (!variant) return;
      if (dockPrice) dockPrice.textContent = money(variant.price, currency);
      const count = normalizeQuantity(quantity?.value);
      if (quantity) quantity.value = count;
      if (add) add.disabled = !variant.available;
      if (addLabel) addLabel.textContent = variant.available ? 'ADD TO CART' : 'SOLD OUT';
    };

    const renderLoading = (item) => {
      const model = item.dataset.model;
      if (dockTitle) dockTitle.textContent = item.dataset.title;
      if (dockModel) dockModel.textContent = model;
      if (dockStatus) dockStatus.textContent = 'Finding live product…';
      if (dockPrice) dockPrice.textContent = '—';
      if (dockUnit) dockUnit.textContent = 'per unit';
      if (dockUrl) dockUrl.href = searchUrl(model);
      if (dockVariant) {
        dockVariant.innerHTML = '<option>Loading…</option>';
        dockVariant.disabled = true;
      }
      if (quantity) {
        quantity.min = item.dataset.defaultMoq || 1;
        quantity.value = item.dataset.defaultMoq || 1;
      }
      if (dockMoq) dockMoq.textContent = `Min. ${item.dataset.defaultMoq || 1}`;
      if (add) {
        add.disabled = true;
        add.dataset.mode = 'cart';
      }
      if (addLabel) addLabel.textContent = 'Loading product…';
    };

    const renderUnavailable = (item) => {
      const model = item.dataset.model;
      if (dockStatus) dockStatus.textContent = 'Open product search';
      if (dockPrice) dockPrice.textContent = 'VIEW';
      if (dockUnit) dockUnit.textContent = 'find this model';
      if (dockVariant) {
        dockVariant.innerHTML = '<option>Product options</option>';
        dockVariant.disabled = true;
      }
      if (dockUrl) dockUrl.href = searchUrl(model);
      if (add) {
        add.disabled = false;
        add.dataset.mode = 'search';
      }
      if (addLabel) addLabel.textContent = 'FIND PRODUCT';
    };

    const renderProduct = (item, state) => {
      const { product, moq } = state;
      const firstAvailable = product.variants.find((variant) => variant.available) || product.variants[0];
      if (dockTitle) dockTitle.textContent = product.title || item.dataset.title;
      if (dockModel) dockModel.textContent = item.dataset.model;
      if (dockStatus) dockStatus.textContent = product.available ? 'Available online' : 'Currently unavailable';
      if (dockUrl) dockUrl.href = product.url || searchUrl(item.dataset.model);
      if (quantity) {
        quantity.min = moq;
        quantity.value = moq;
      }
      if (dockMoq) dockMoq.textContent = `Min. ${moq}`;
      if (dockVariant) {
        dockVariant.innerHTML = '';
        product.variants.forEach((variant) => {
          const option = document.createElement('option');
          option.value = variant.id;
          option.textContent = variant.title === 'Default Title' ? 'Standard option' : variant.title;
          if (!variant.available) option.textContent += ' · Sold out';
          option.disabled = !variant.available;
          option.selected = String(variant.id) === String(firstAvailable?.id);
          dockVariant.appendChild(option);
        });
        dockVariant.disabled = product.variants.length <= 1;
      }
      if (add) add.dataset.mode = 'cart';
      refreshPrice();
    };

    const candidateScore = (candidate, model) => {
      const needle = normalize(model);
      const title = normalize(candidate.title);
      const handle = normalize(candidate.handle || candidate.url);
      if (title === needle || handle === needle) return 100;
      if (title.endsWith(needle) || handle.endsWith(needle)) return 90;
      if (title.includes(needle) || handle.includes(needle)) return 70;
      return 0;
    };

    const fetchProduct = async (index) => {
      if (productCache.has(index)) return productCache.get(index);
      if (loading.has(index)) return loading.get(index);

      const item = items[index];
      const model = item.dataset.model;
      const request = (async () => {
        try {
          const params = new URLSearchParams({ q: model });
          params.set('resources[type]', 'product');
          params.set('resources[limit]', '10');
          params.set('resources[options][unavailable_products]', 'show');
          const suggestionResponse = await fetch(`${root}search/suggest.json?${params.toString()}`, { headers: { Accept: 'application/json' } });
          if (!suggestionResponse.ok) throw new Error('Product search unavailable');
          const suggestionData = await suggestionResponse.json();
          const candidates = suggestionData?.resources?.results?.products || [];
          const candidate = [...candidates].sort((a, b) => candidateScore(b, model) - candidateScore(a, model))[0];
          if (!candidate || candidateScore(candidate, model) === 0) throw new Error(`No exact match for ${model}`);

          const productPath = String(candidate.url || `${root}products/${candidate.handle}`).split('?')[0].replace(/\.js$/, '');
          const productResponse = await fetch(`${productPath}.js`, { headers: { Accept: 'application/json' } });
          if (!productResponse.ok) throw new Error('Product details unavailable');
          const product = await productResponse.json();
          product.url = productPath;
          const state = { product, moq: minimumQuantity(product, item.dataset.defaultMoq) };
          productCache.set(index, state);
          return state;
        } catch (error) {
          const state = { error };
          productCache.set(index, state);
          return state;
        } finally {
          loading.delete(index);
        }
      })();

      loading.set(index, request);
      return request;
    };

    const positionItems = () => {
      const positionClasses = ['is-center', 'is-right-near', 'is-right-far', 'is-left-far', 'is-left-near'];
      items.forEach((item, index) => {
        item.classList.remove(...positionClasses);
        const offset = (index - active + items.length) % items.length;
        item.classList.add(positionClasses[offset]);
        item.setAttribute('aria-pressed', String(index === active));
        item.tabIndex = index === active ? 0 : -1;
      });
      rail.forEach((button, index) => {
        const isActive = index === active;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-selected', String(isActive));
        button.tabIndex = isActive ? 0 : -1;
      });
      if (counter) counter.textContent = String(active + 1).padStart(2, '0');
    };

    const activate = async (index, interaction = false) => {
      active = (index + items.length) % items.length;
      if (interaction) userInteracted = true;
      positionItems();
      const item = items[active];
      const cached = productCache.get(active);
      if (cached?.product) renderProduct(item, cached);
      else if (cached?.error) renderUnavailable(item);
      else renderLoading(item);
      const railButton = rail[active];
      const railViewport = railButton?.parentElement;
      if (railButton && railViewport && railViewport.scrollWidth > railViewport.clientWidth) {
        const targetLeft = railButton.offsetLeft - (railViewport.clientWidth - railButton.offsetWidth) / 2;
        railViewport.scrollTo({
          left: Math.max(0, targetLeft),
          behavior: reduceMotion ? 'auto' : 'smooth'
        });
      }
      const state = await fetchProduct(active);
      if (items[active] !== item) return;
      if (state.product) renderProduct(item, state);
      else renderUnavailable(item);
    };

    items.forEach((item, index) => item.addEventListener('click', () => activate(index, true)));
    rail.forEach((button, index) => {
      button.addEventListener('click', () => activate(index, true));
      button.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        const delta = event.key === 'ArrowRight' ? 1 : -1;
        const target = (index + delta + rail.length) % rail.length;
        activate(target, true).then(() => rail[target]?.focus());
      });
    });
    previous?.addEventListener('click', () => activate(active - 1, true));
    next?.addEventListener('click', () => activate(active + 1, true));

    dockVariant?.addEventListener('change', refreshPrice);
    quantity?.addEventListener('change', refreshPrice);
    hero.querySelector('[data-quantity-minus]')?.addEventListener('click', () => {
      if (!quantity) return;
      quantity.value = normalizeQuantity(Number(quantity.value) - 1);
    });
    hero.querySelector('[data-quantity-plus]')?.addEventListener('click', () => {
      if (!quantity) return;
      quantity.value = normalizeQuantity(Number(quantity.value) + 1);
    });

    add?.addEventListener('click', async () => {
      const item = items[active];
      if (add.dataset.mode === 'search') {
        window.location.href = searchUrl(item.dataset.model);
        return;
      }

      const variant = selectedVariant();
      const count = normalizeQuantity(quantity?.value);
      if (!variant?.available || add.dataset.loading === 'true') return;
      add.dataset.loading = 'true';
      add.disabled = true;
      if (addLabel) addLabel.textContent = 'ADDING…';

      try {
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variant.id, quantity: count }] })
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || 'Unable to add this product.');
        const cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((value) => value.json());
        document.querySelectorAll('.cart-count, [data-cart-count]').forEach((badge) => { badge.textContent = cart.item_count; });
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
        if (addLabel) addLabel.textContent = 'ADDED TO CART';
        announce(`${count} × ${item.dataset.model} added to cart.`);
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'add_to_cart', product_id: productCache.get(active)?.product?.id, variant_id: variant.id, quantity: count });
        setTimeout(() => { if (addLabel) addLabel.textContent = 'ADD TO CART'; }, 1800);
      } catch (error) {
        if (addLabel) addLabel.textContent = 'ADD TO CART';
        announce(error.message || 'Unable to add this product. Please try again.');
      } finally {
        add.dataset.loading = 'false';
        add.disabled = !selectedVariant()?.available;
      }
    });

    if (scene && finePointer && !reduceMotion) {
      scene.addEventListener('pointermove', (event) => {
        const bounds = scene.getBoundingClientRect();
        const x = (event.clientX - bounds.left) / bounds.width - .5;
        const y = (event.clientY - bounds.top) / bounds.height - .5;
        background?.style.setProperty('--hero-x', `${x * -8}px`);
        background?.style.setProperty('--hero-y', `${y * -5}px`);
      }, { passive: true });
      scene.addEventListener('pointerleave', () => {
        background?.style.setProperty('--hero-x', '0px');
        background?.style.setProperty('--hero-y', '0px');
      });
    }

    scene?.addEventListener('touchstart', (event) => { touchStart = event.changedTouches[0]?.clientX || null; }, { passive: true });
    scene?.addEventListener('touchend', (event) => {
      if (touchStart === null) return;
      const distance = (event.changedTouches[0]?.clientX || touchStart) - touchStart;
      touchStart = null;
      if (Math.abs(distance) < 45) return;
      activate(active + (distance < 0 ? 1 : -1), true);
    }, { passive: true });

    const startAuto = () => {
      if (reduceMotion) return;
      clearInterval(autoTimer);
      autoTimer = setInterval(() => { if (!userInteracted) activate(active + 1); }, 6500);
    };
    scene?.addEventListener('mouseenter', () => clearInterval(autoTimer));
    scene?.addEventListener('mouseleave', startAuto);
    hero.addEventListener('focusin', () => clearInterval(autoTimer));
    hero.addEventListener('focusout', startAuto);

    positionItems();
    activate(0);
    items.forEach((_, index) => { if (index !== 0) fetchProduct(index); });
    startAuto();
  };

  const initialize = (root = document) => root.querySelectorAll('[data-commerce-hero]').forEach(initHero);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize());
  else initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
